<template>
  <!-- Workspace369's own phone proof for compare, alternatives and switch pages (growth #12):
       three screenshots from the App Store listing, the /features/mobile-app points word for
       word, and the listing's device rows with the date they were checked. The host page
       renders the section and its heading; this block lays itself out by its own width. -->
  <div class="map-host">
    <div class="map">
      <figure class="map-shots">
        <div class="map-shot-row">
          <img
            v-for="shot in workspaceMobileScreenshots"
            :key="shot.src"
            :src="shot.src"
            :srcset="`${shot.srcSmall} 300w, ${shot.src} 600w`"
            sizes="(max-width: 639px) 30vw, 11rem"
            :alt="shot.alt"
            width="600"
            height="1304"
            loading="lazy"
            decoding="async"
            class="map-shot"
          />
        </div>
        <figcaption>Screenshots from Workspace369’s App Store listing.</figcaption>
      </figure>
      <div class="map-copy">
        <p class="map-lead">One workspace on the web, iPhone and iPad. What the app does today:</p>
        <ul class="map-points">
          <li v-for="point in workspaceMobileApp.points" :key="point">{{ point }}</li>
        </ul>
        <div class="map-store">
          <p class="map-store-title">App Store listing, checked {{ workspaceMobileApp.store.checkedOn }}</p>
          <dl>
            <div v-for="row in workspaceMobileApp.store.rows" :key="row.device">
              <dt>{{ row.device }}</dt>
              <dd>{{ row.requirement }}</dd>
            </div>
          </dl>
        </div>
        <p class="map-links">
          <NuxtLink
            :to="localePath(workspaceMobileApp.featurePath)"
            class="map-link"
            data-conversion-event="mobile_app_proof_click"
            data-conversion-label="Mobile app proof feature link"
            :data-conversion-location="location"
          >See the mobile app →</NuxtLink>
          <a
            :href="workspaceMobileApp.store.url"
            target="_blank"
            rel="noopener noreferrer"
            class="map-link map-link-quiet"
            data-conversion-event="mobile_app_proof_click"
            data-conversion-label="Mobile app proof App Store link"
            :data-conversion-location="location"
          >{{ workspaceMobileApp.store.label }} ↗</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { workspaceMobileApp, workspaceMobileScreenshots } from '~/data/mobile-app-facts';

defineProps({
  location: { type: String, required: true },
});

const { localePath } = useWorkspaceLocale();
</script>

<style scoped>
.map-host { container-type: inline-size; }

.map {
  display: grid;
  gap: 1.75rem;
}

.map-shots { margin: 0; }

.map-shot-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
  align-items: start;
}

.map-shot {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid #dbe4ee;
  border-radius: 10px;
  background: #eaf3fa;
  box-shadow: 0 14px 30px -18px rgb(15 23 42 / 0.35);
}

.map-shot:nth-child(2) { margin-top: 1.1rem; }

.map-shots figcaption {
  margin-top: 0.75rem;
  color: #6b7280;
  font-size: 0.76rem;
  line-height: 1.5;
}

.map-lead {
  color: #374151;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.7;
}

.map-points {
  margin-top: 1rem;
  display: grid;
  gap: 0.55rem;
  padding: 0;
  list-style: none;
}

.map-points li {
  position: relative;
  padding-left: 1.55rem;
  color: #1f2937;
  font-size: 0.94rem;
  line-height: 1.55;
}

/* A teal check, drawn in CSS so the list needs no icon font. */
.map-points li::before {
  content: "";
  position: absolute;
  left: 0.2rem;
  top: 0.38em;
  width: 0.42rem;
  height: 0.72rem;
  border: solid #0e7490;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.map-store {
  margin-top: 1.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 0.85rem 1rem;
}

.map-store-title {
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 700;
}

.map-store dl { margin-top: 0.45rem; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 0.3rem 0.75rem; font-size: 0.88rem; }
.map-store dl div { display: contents; }
.map-store dt { color: #111827; font-weight: 800; }
.map-store dd { margin: 0; color: #374151; }

.map-links {
  margin-top: 0.4rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0 1.4rem;
}

.map-link {
  /* 44px+ tap targets on phones (they were 28px); the padding replaces the row gap and top margin. */
  padding-block: 0.7rem;
  color: #0e7490;
  font-size: 0.92rem;
  font-weight: 800;
  text-decoration: underline;
  text-decoration-color: rgb(14 116 144 / 0.35);
  text-underline-offset: 3px;
}

.map-link:hover { text-decoration-color: currentColor; }
.map-link-quiet { color: #374151; font-weight: 700; }
.map-link:focus-visible { outline: 2px solid #0e7490; outline-offset: 3px; border-radius: 2px; }

/* Wide hosts (compare and switch pages): screenshots beside the copy. */
@container (min-width: 44rem) {
  .map { grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr); gap: 2.5rem; align-items: start; }
  .map-shots { order: 2; }
}
</style>
